import { Component, Inject, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { firstValueFrom } from 'rxjs';
import { TranslationModule } from '@core/translation/translation.module';
import {
    AppDataShareStatusResult,
    BcmAppDataShareApiService,
} from '@bcmServices/api-services/app-data-share.api-service';

@Component({
    selector: 'app-data-share-wait-dialog',
    standalone: true,
    imports: [MatDialogModule, MatButtonModule, MatProgressSpinnerModule, TranslationModule],
    template: `
        <h2 mat-dialog-title>{{ 'person.core.up2boatIdWaitingTitle' | translate:{}:'Warte auf Freigabe' }}</h2>
        <div mat-dialog-content>
            <div style="display:flex;gap:16px;align-items:center;">
                <mat-spinner diameter="36"></mat-spinner>
                <div>
                    <p>{{ 'person.core.up2boatIdWaitingBody' | translate:{}:'Der App-Nutzer bestätigt die Übernahme in der Up2Boat App. Bis dahin werden keine Daten angezeigt.' }}</p>
                    <p><strong>{{ secondsLeft }}</strong> s</p>
                </div>
            </div>
        </div>
        <div mat-dialog-actions align="end">
            <button mat-button type="button" (click)="cancel()">
                {{ 'person.core.up2boatIdCancel' | translate:{}:'Anfrage abbrechen' }}
            </button>
        </div>
    `
})
export class AppDataShareWaitDialogComponent implements OnDestroy {
    secondsLeft = 60;
    private timer?: ReturnType<typeof setInterval>;
    private closed = false;

    constructor(
        private readonly dialogRef: MatDialogRef<AppDataShareWaitDialogComponent, AppDataShareStatusResult | null>,
        private readonly api: BcmAppDataShareApiService,
        @Inject(MAT_DIALOG_DATA) private readonly data: { requestUuid: string; expiresAt: string }
    ) {
        const expiresAt = new Date(data.expiresAt).getTime();
        this.secondsLeft = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
        this.timer = setInterval(() => {
            this.secondsLeft = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
            void this.poll();
        }, 1000);
        void this.poll();
    }

    ngOnDestroy(): void {
        if (this.timer) {
            clearInterval(this.timer);
        }
    }

    async cancel(): Promise<void> {
        try {
            await firstValueFrom(this.api.cancelRequest(this.data.requestUuid));
        } catch {
            // The request may already be finished.
        }

        this.finish({ requestUuid: this.data.requestUuid, state: 'cancelled', dataType: 'userProfile' });
    }

    private async poll(): Promise<void> {
        if (this.closed) {
            return;
        }

        try {
            const status = await firstValueFrom(this.api.getRequestStatus(this.data.requestUuid));

            if (status.state !== 'pending') {
                this.finish(status);
            }
        } catch {
            this.finish(null);
        }
    }

    private finish(status: AppDataShareStatusResult | null): void {
        if (this.closed) {
            return;
        }

        this.closed = true;

        if (this.timer) {
            clearInterval(this.timer);
        }

        this.dialogRef.close(status);
    }
}
